import { Suspense, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { ScreenLoading } from "@/components/ScreenLoading";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { loadChronicle } from "@/data/chronicles/registry";
import { CHRONICLE_SAVE_KEYS } from "@/data/chronicles/save-keys";

// ════════════════════════════════════════════════════════════════
// Ambient-music data tables — CONSUMED BY App-level RouterAware
// AmbientMusic wrapper, NOT by this file.
// ════════════════════════════════════════════════════════════════
//
// As of 2026-05 the AmbientMusicProvider has been moved out of this
// layout and up to the App level (src/App.tsx, wrapping the Routes)
// so route navigation cannot unmount it. The CHRONICLE_AUDIO and
// PER_ORIGIN_AUDIO tables below remain exported and are consumed
// by App.tsx via RouterAwareAmbientMusic. They live here only for
// historical / co-located reasons — moving them to a separate
// module would be cleaner.
//
// DO NOT add an AmbientMusicProvider back into ChronicleLayout.
// See the warning at the top of src/components/game/AmbientMusic.tsx.

export const CHRONICLE_AUDIO: Record<string, { src: string; startOffset?: number }> = {
  "eighty-years-war": { src: "/audio/eighty-years-war-ambient.mp3" },
  "medici-florence": { src: "/audio/medici-florence-ambient.mp3" },
  "italian-city-states": { src: "/audio/italian-city-states-ambient.mp3" },
  "italian-wars": { src: "/audio/italian-wars-ambient.mp3" },
  "italian-unification": { src: "/audio/italian-unification-ambient.mp3" },
  "dutch-golden-age-voc": { src: "/audio/dutch-golden-age-ambient.mp3" },
  "burgundian-netherlands": { src: "/audio/burgundian-netherlands-ambient.mp3" },
  "belgian-revolution": { src: "/audio/belgian-revolution-ambient.mp3", startOffset: 5 },
  "reconquista-expansion": { src: "/audio/reconquista-ambient.mp3" },
  "fall-of-granada": { src: "/audio/fall-of-granada-ambient.mp3" },
  "first-voyages-west": { src: "/audio/first-voyages-west-ambient.mp3" },
  "iberian-union": { src: "/audio/iberian-union-ambient.mp3" },
  "peninsular-war": { src: "/audio/peninsular-war-ambient.mp3" },
  "spanish-civil-war": { src: "/audio/spanish-civil-war-ambient.mp3" },
  "hundred-years-war": { src: "/audio/hundred-years-war-ambient.mp3" },
  "french-revolution": { src: "/audio/french-revolution-ambient.mp3", startOffset: 3 },
  "second-republic": { src: "/audio/second-republic-ambient.mp3" },
  "albigensian-crusade": { src: "/audio/albigensian-crusade-ambient.mp3" },
  "viking-dublin": { src: "/audio/viking-dublin-ambient.mp3" },
  "norman-conquest": { src: "/audio/norman-conquest-ambient.mp3" },
  "the-anarchy": { src: "/audio/the-anarchy-ambient.mp3" },
  "wars-of-the-roses": { src: "/audio/wars-of-the-roses-ambient.mp3" },
  "english-civil-wars": { src: "/audio/english-civil-wars-ambient.mp3" },
  "irish-independence": { src: "/audio/irish-independence-ambient.mp3" },
  "christianization-scandinavia": { src: "/audio/christianization-scandinavia-ambient.mp3" },
  "livonian-crusade": { src: "/audio/livonian-crusade-ambient.mp3" },
  "mongol-sack-of-rus": { src: "/audio/mongol-invasions-ambient.mp3" },
  "the-deluge": { src: "/audio/the-deluge-ambient.mp3" },
  "kalmar-union": { src: "/audio/kalmar-union-ambient.mp3" },
  "siege-of-constantinople": { src: "/audio/siege-of-constantinople-ambient.mp3" },
  "ottoman-expansion-europe": { src: "/audio/ottoman-expansion-ambient.mp3" },
  "serbian-uprisings": { src: "/audio/serbian-uprisings-ambient.mp3" },
  "greek-independence": { src: "/audio/greek-independence-ambient.mp3" },
  "carolingian-empire": { src: "/audio/carolingian-empire-ambient.mp3" },
  // The Black Death (pan-European) — no track yet; the layout handles a
  // missing mp3 silently. A period-appropriate placeholder would draw on
  // 14th-c. plainchant (the Dies Irae sequence, a Requiem introit) for the
  // years of the Great Mortality. When supplied, measure its LUFS per
  // CLAUDE.md and add a TRACK_GAIN entry.
  "black-death": { src: "/audio/black-death-ambient.mp3" },
  // Magyar Raids — no track yet. A period-appropriate placeholder would
  // draw on reconstructed steppe / Eurasian-pentatonic music (the
  // horsehead fiddle, the regös tradition) for the rider's world. The
  // layout handles a missing mp3 silently; when a file is supplied,
  // measure its LUFS per CLAUDE.md §9 and add a TRACK_GAIN entry.
  "magyar-raids": { src: "/audio/magyar-raids-ambient.mp3" },
  // Thirty Years' War — no track yet. A period-appropriate placeholder would
  // draw on early-baroque German / Bohemian music (a lament, a slow sarabande)
  // for the war's burned-country world. The layout handles a missing mp3
  // silently; when a file is supplied, measure its LUFS per CLAUDE.md §9 and
  // add a TRACK_GAIN entry.
  "thirty-years-war": { src: "/audio/thirty-years-war-ambient.mp3" },
  // Hussite Wars — placeholder ambient; the layout handles a missing mp3
  // silently. When a track is supplied, measure its LUFS per CLAUDE.md §9.
  "hussite-wars": { src: "/audio/hussite-wars-ambient.mp3" },
  "investiture-controversy": { src: "/audio/investiture-controversy-ambient.mp3" },
  // Unification of Germany — placeholder ambient; the layout handles a missing
  // mp3 silently. When a track is supplied, measure its LUFS per CLAUDE.md §9.
  "german-unification": { src: "/audio/german-unification-ambient.mp3" },
  // Great Northern War — neutral chronicle ambient (the Swedish
  // "Kongliga Gardets Marsch", soft and period-appropriate) for the
  // chronicle's overview + character-select screens. The per-protagonist
  // marches — the Karoliner / Swedish march and the Russian Preobrazhensky
  // — switch in on /play via the per-origin useEffect in src/pages/Play.tsx
  // (lines 241-252). This matches every other multi-protagonist chronicle's
  // "one base + optional in-play override" pattern and makes the moment of
  // selecting an origin a real musical commitment.
  "great-northern-war": { src: "/audio/great-northern-war-ambient.mp3" },
  "settlement-of-iceland": { src: "/audio/settlement-of-iceland-ambient.mp3" },
  // World War One — neutral ambient for the pre-selection screens
  // (chronicle overview + the two-card character select). Optional;
  // ChronicleLayout handles a missing mp3 silently. Per-protagonist
  // tracks (a French or Prussian period piece) can be wired in via
  // the same switchTrack pattern as GNW if desired.
  "world-war-one": { src: "/audio/world-war-one-ambient.mp3" },
  // Russian Revolution — neutral chronicle ambient for the chronicle
  // overview + the two-card protagonist select. Per-protagonist
  // tracks (Red Pyotr / White Sergei) switch in on /play via the
  // per-origin useEffect in src/pages/Play.tsx (the same pattern as
  // GNW and WWI). PER_ORIGIN_AUDIO entries below.
  "russian-revolution": { src: "/audio/russian-revolution-ambient.mp3" },
  // Hungarian Revolution 1956 — neutral chronicle ambient. The
  // 13-day rising has no canonical anthem (the Petőfi song
  // "Talpra magyar" of 1848 was banned by the regime in 1948 and
  // unbanned in 1956 during the rising itself, which makes it the
  // closest historical match if a music file is later supplied).
  "hungarian-revolution-1956": { src: "/audio/hungarian-revolution-1956-ambient.mp3" },
  // The 1848 chronicle's ambient track will likely draw on the music of
  // the reform era — the Rákóczi March, or Erkel's operas that gave the
  // national movement its sound — if a file is later supplied. The layout
  // handles a missing mp3 silently; single-protagonist, so no PER_ORIGIN_AUDIO.
  "hungarian-revolution-1848": { src: "/audio/hungarian-revolution-1848-ambient.mp3" },
  // The Prague Spring's ambient track will likely be drawn from
  // the late-1960s Czech 'big beat' scene the reform thaw allowed
  // back into the airwaves — the Plastic People, Karel Kryl, the
  // Czech-language pop the censor's blue pencil had to step around
  // after 1968. Placeholder path until a file is supplied.
  "prague-spring-1968": { src: "/audio/prague-spring-1968-ambient.mp3" },
  // Two Germanies — neutral chronicle ambient. The 45-year arc has
  // no canonical anthem; period-appropriate options would include
  // Bach's Leipzig works (St Matthew Passion or the Goldberg
  // Variations) for the long-historical feel, or an Eisler / Weill
  // workers'-song selection for the 1945-50 founding period.
  // Placeholder path until a file is supplied.
  "two-germanies": { src: "/audio/two-germanies-ambient.mp3" },
  // Fall of Communism — the Velvet Revolution. A period-appropriate
  // track would draw on the late-1980s Czech protest-song scene the
  // regime could no longer silence (Karel Kryl's returned-exile songs,
  // Marta Kubišová's "Modlitba pro Martu" — the banned 1968 anthem the
  // crowds sang again in November 1989). Placeholder path until a file
  // is supplied.
  "fall-of-communism": { src: "/audio/fall-of-communism-ambient.mp3" },
  // The Protestant Reformation — neutral chronicle ambient. A period-
  // appropriate track would draw on the age's sacred music: a Lutheran
  // chorale (Luther's own "Ein feste Burg ist unser Gott", c. 1529) for
  // the evangelical side, or a Palestrina / plainchant setting for the
  // Roman. Placeholder path until a file is supplied; handled silently if
  // missing.
  "protestant-reformation": { src: "/audio/protestant-reformation-ambient.mp3" },
  // The Napoleonic Wars — no track yet; the layout handles a missing mp3
  // silently. A period-appropriate placeholder would draw on the era's
  // military and concert music (a Napoleonic-era French marche, or the
  // solemn end of Beethoven's Eroica, the symphony whose dedication to
  // Bonaparte he tore up when the man crowned himself). When supplied,
  // measure its LUFS per CLAUDE.md §9 and add a TRACK_GAIN entry.
  "napoleonic-wars": { src: "/audio/napoleonic-wars-ambient.mp3" },
  // The Second World War in Europe — dignified, NON-triumphalist ambient
  // (no martial glory, no swelling heroic score). The layout handles a
  // missing mp3 silently; the file may not exist yet. Per-origin elegiac
  // registers below. When supplied, measure LUFS per CLAUDE.md §9 and add
  // a TRACK_GAIN entry. For Rosa's chapters especially: restraint,
  // near-silence — never scoring over deportation.
  "world-war-two-europe": { src: "/audio/world-war-two-europe-ambient.mp3", startOffset: 3 },
};

// Per-origin overrides for multi-protagonist chronicles. When a save
// with the matching origin flag exists in localStorage, the layout
// initialises the AmbientMusicProvider with the origin's track
// DIRECTLY, instead of starting on the neutral base and trying to
// switchTrack from Play.tsx (which races the browser autoplay-gesture
// budget — a click only buys one audio.play() call, and if the layout
// has already spent that gesture on the neutral track, the per-origin
// switchTrack call from Play.tsx silently fails). The Play.tsx
// switchTrack call still runs to handle in-session origin changes,
// but the home-screen Continue chip now resumes directly on the
// right track.
export const PER_ORIGIN_AUDIO: Record<string, Record<string, string>> = {
  "great-northern-war": {
    origin_karoliner: "/audio/great-northern-war-swedish-ambient.mp3",
    origin_russian:   "/audio/great-northern-war-russian-ambient.mp3",
  },
  "world-war-one": {
    origin_bef:      "/audio/world-war-one-bef-ambient.mp3",
    origin_poilu:    "/audio/world-war-one-poilu-ambient.mp3",
    origin_prussian: "/audio/world-war-one-prussian-ambient.mp3",
  },
  "russian-revolution": {
    origin_red:   "/audio/russian-revolution-red-ambient.mp3",
    origin_white: "/audio/russian-revolution-white-ambient.mp3",
  },
  "napoleonic-wars": {
    origin_grognard:   "/audio/napoleonic-wars-grognard-ambient.mp3",
    origin_prussian:   "/audio/napoleonic-wars-prussian-ambient.mp3",
    origin_bluejacket: "/audio/napoleonic-wars-bluejacket-ambient.mp3",
  },
  // Four elegiac per-origin registers. Files may not exist yet; the layout
  // handles a missing mp3 silently. Rosa's especially must be near-silent
  // restraint — never a score over the persecution.
  // Kaspar, the soldier: "Unser liebe Fraue", a Landsknecht marching song,
  // plays for his whole road. Anežka keeps the chronicle's own track.
  "thirty-years-war": {
    origin_soldier: "/audio/thirty-years-war-soldier-ambient.mp3",
  },
  "world-war-two-europe": {
    origin_witness:       "/audio/world-war-two-europe-witness-ambient.mp3",
    origin_occupe:        "/audio/world-war-two-europe-occupe-ambient.mp3",
    origin_blitz:         "/audio/world-war-two-europe-blitz-ambient.mp3",
    origin_stiller_held:  "/audio/world-war-two-europe-stiller-held-ambient.mp3",
  },
};

export function readSavedOriginTrack(chronicleId: string | undefined): string | null {
  if (!chronicleId) return null;
  const overrides = PER_ORIGIN_AUDIO[chronicleId];
  if (!overrides) return null;
  const saveKey = CHRONICLE_SAVE_KEYS[chronicleId];
  if (!saveKey) return null;
  try {
    const raw = localStorage.getItem(saveKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // GameState's `flags` is a Set serialised as an array on save.
    const flags: string[] = Array.isArray(parsed?.flags) ? parsed.flags : [];
    for (const flag of flags) {
      if (overrides[flag]) return overrides[flag];
    }
  } catch {
    // Corrupt save; fall through to the neutral base.
  }
  return null;
}

// Resolve a chronicle + short origin id ("bef", "poilu", "red",
// "karoliner", …) to its per-origin ambient track, or null when the
// chronicle has no per-origin override for that origin. This is the
// SINGLE source of truth shared by Play.tsx's in-play switchTrack effect
// AND its restart / Play-Again handlers, so the neutral base can never
// leak into the story on a replay or a post-death restart — the bug that
// came from the threshold-restart path calling resetTrack() while the
// in-play effect (keyed on a now-unchanged origin) never re-fired to put
// the origin track back.
export function originTrackSrc(
  chronicleId: string | undefined,
  origin: string | null,
): string | null {
  if (!chronicleId || !origin) return null;
  return PER_ORIGIN_AUDIO[chronicleId]?.["origin_" + origin] ?? null;
}

export default function ChronicleLayout() {
  const { id } = useParams();

  // Warm the chronicle's lazy chunk (engine + chapters + news + map
  // data + map component + entity-config + relationships) on layout
  // mount. By the time the player clicks "Begin Chronicle" and
  // navigates to /play, the chunk is already in cache and Play.tsx
  // mounts synchronously — no blank Suspense flash. Fire-and-forget;
  // any failure is caught by Play's own Suspense + the route-level
  // ErrorBoundary if the chunk genuinely fails to load.
  //
  // (Briefly added route-chunk preloads here too in May 2026 but
  // reverted — they didn't reduce the perceived music hiccup between
  // chronicle screens and added concurrent network traffic that
  // appeared to compete with the audio fetch on first chronicle
  // entry.)
  useEffect(() => {
    if (!id) return;
    loadChronicle(id).catch(() => {});
  }, [id]);

  // The Outlet children (ChronicleDetail / ProtagonistIntro / Play)
  // are lazy-loaded. The inner Suspense boundary here catches their
  // chunk loads so the loading state is the small ScreenLoading
  // overlay, not the App-level fallback. ErrorBoundary catches
  // engine/map-component render errors without tearing down the
  // layout.
  //
  // Note: this layout NO LONGER wraps the children in
  // AmbientMusicProvider. As of 2026-05 the provider lives at the
  // App level (src/App.tsx → RouterAwareAmbientMusic), one level
  // above the Routes, so route navigation cannot unmount it. See
  // the warning at the top of src/components/game/AmbientMusic.tsx
  // for why this matters.
  return (
    <ErrorBoundary label="chronicle-layout">
      <Suspense fallback={<ScreenLoading />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}
